"use client";

import type { ReactNode } from "react";
import { createContext, use, useCallback, useMemo, useState } from "react";
import { useToggle } from "@/hooks/ui/use-toggle";
import type {
  Notification,
  NotificationCategory,
  NotificationContextValue,
  SystemAlert,
} from "./types";

const NotificationContext = createContext<NotificationContextValue | null>(
  null,
);

// FIFO cap so a chatty realtime stream (missed-call burst, batch import,
// agent escalations) can't grow the in-memory array unboundedly. 200 is
// well above what fits on screen and still bounds the worst-case render.
const MAX_NOTIFICATIONS = 200;

function generateId(): string {
  return `notif-${String(Date.now())}-${Math.random().toString(36).slice(2, 9)}`;
}

interface NotificationProviderProps {
  children: ReactNode;
  /** Initial notifications for testing/demo */
  initialNotifications?: Notification[];
}

export function NotificationProvider({
  children,
  initialNotifications = [],
}: NotificationProviderProps) {
  const [notifications, setNotifications] =
    useState<Notification[]>(initialNotifications);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);
  const [
    isOpen,
    { setTrue: openDrawer, setFalse: closeDrawer, toggle: toggleDrawer },
  ] = useToggle();
  const [activeCategory, setActiveCategory] =
    useState<NotificationCategory>("all");

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length + systemAlerts.length,
    [notifications, systemAlerts],
  );

  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
      const newNotification: Notification = {
        ...notification,
        id: generateId(),
        timestamp: new Date(),
        read: false,
      };
      setNotifications((prev) => {
        const next = [newNotification, ...prev];
        return next.length > MAX_NOTIFICATIONS
          ? next.slice(0, MAX_NOTIFICATIONS)
          : next;
      });
    },
    [],
  );

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const registerSystemAlert = useCallback((alert: SystemAlert) => {
    setSystemAlerts((prev) => {
      const idx = prev.findIndex((a) => a.key === alert.key);
      if (idx === -1) return [...prev, alert];
      // Update in place if content changed
      if (prev[idx].title === alert.title && prev[idx].body === alert.body)
        return prev;
      const next = [...prev];
      next[idx] = alert;
      return next;
    });
  }, []);

  const unregisterSystemAlert = useCallback((key: string) => {
    setSystemAlerts((prev) => {
      const next = prev.filter((a) => a.key !== key);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const value: NotificationContextValue = useMemo(
    () => ({
      notifications,
      systemAlerts,
      unreadCount,
      isOpen,
      activeCategory,
      addNotification,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      clearAll,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      setActiveCategory,
      registerSystemAlert,
      unregisterSystemAlert,
    }),
    [
      notifications,
      systemAlerts,
      unreadCount,
      isOpen,
      activeCategory,
      addNotification,
      markAsRead,
      markAllAsRead,
      dismissNotification,
      clearAll,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      registerSystemAlert,
      unregisterSystemAlert,
    ],
  );

  return <NotificationContext value={value}>{children}</NotificationContext>;
}

export function useNotifications(): NotificationContextValue {
  const context = use(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
}

/**
 * Non-throwing variant. `NotificationProvider` only wraps the authenticated
 * shell (providers/app-shell-realtime.tsx), so root-layout components that
 * also render on /auth, marketing, and other public routes must be able to ask
 * for the context and get `null` instead of crashing the whole tree.
 */
export function useOptionalNotifications(): NotificationContextValue | null {
  return use(NotificationContext);
}
